import BaseApiClient from '../BaseApiClient';
import {
  AudienceInterface,
  CreateUpdateAudiencePayload,
  ListLabel,
  ListValueInterface
} from '@/interfaces/Audience.interface';
import { ListDataParams, ListDataResponse } from '../types';
import { CustomFieldInterface } from '@/interfaces/CustomFields.interface';

const API_VERSION = { Version: '2025-11-14' };

export class ApiAudiences extends BaseApiClient {
  async downloadTemplate(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/lists/template/download', {
          headers: API_VERSION,
          responseType: 'blob'
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Get audiences
   */
  async list(
    listData: ListDataParams
  ): Promise<ListDataResponse<AudienceInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/lists', listData, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data ?? [],
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get audience
   */
  async get(id: number): Promise<AudienceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/lists/${id}`, { headers: API_VERSION })
        .then((res) => {
          resolve(res.data.data);
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create audience
   */
  async create(
    payload: Partial<CreateUpdateAudiencePayload>
  ): Promise<AudienceInterface> {
    const formData = new FormData();

    for (const [key, value] of Object.entries(payload).filter(
      ([key]) => key !== 'fields'
    )) {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, String(item));
          });
        } else {
          formData.append(key, String(value));
        }
      }
    }

    if (payload.fields) {
      payload.fields.forEach((value, index) => {
        formData.append(`fields[${index}][label]`, value.label);
        formData.append(
          `fields[${index}][field_type_id]`,
          value.field_type_id?.toString() ?? ''
        );
        value.field_attribute_id &&
          formData.append(
            `fields[${index}][field_attribute_id]`,
            value.field_attribute_id?.toString() ?? ''
          );
        formData.append(`fields[${index}][order]`, value.order.toString());
      });
    }

    return new Promise((resolve, reject) => {
      this.client
        .post('/lists', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...API_VERSION
          }
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update audience
   */
  async update(
    id: number,
    payload: Partial<CreateUpdateAudiencePayload>
  ): Promise<AudienceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/lists/${id}`, payload, {
          headers: API_VERSION
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete audience
   */
  async delete(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/lists/${id}`, { headers: API_VERSION })
        .then((res) => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Restore audience
   */
  async restore(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/lists/${id}/restore`, {}, { headers: API_VERSION })
        .then((res) => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get field group list types
   */
  async getFieldGroupListTypes(
    listData: ListDataParams
  ): Promise<ListDataResponse<CustomFieldInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('field-groups/list-types', listData, {
          headers: API_VERSION
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data ?? [],
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get audience list values
   */
  async audienceListValues(
    id: number,
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<ListValueInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.value = searchTerm;
      }
      this.client
        .getList(`/lists/${id}/list-values`, listData, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update list values
   */
  async updateListValues(payload: {
    list_values: {
      id: number;
      value:
        | string
        | number
        | number[]
        | { id: number; is_checked: boolean; order: number }[]
        | null;
    }[];
  }): Promise<ListValueInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .put('/list-values', payload, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Update list value
   */
  async updateListValue(
    id: number,
    payload: { value: string | number | null }
  ): Promise<ListValueInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/list-values/${id}`, payload, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete list values
   */
  async deleteListValues(id: number, ids: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/list-values/${id}`, {
          data: { list_values: ids },
          headers: API_VERSION
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  /**
   * Get audience list filters
   */
  async getListFilters(id: number): Promise<
    {
      id: number;
      name: string;
      values: {
        id: number;
        field_id: number;
        label: string;
        value: string;
      }[];
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/list-filters/${id}/lists`, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Create or update list filter
   */
  async createOrUpdateListFilter(
    id: number,
    payload: {
      id?: number;
      name: string;
      fields: {
        label: string;
        field_type_id: number;
        value: string;
      }[];
    }
  ): Promise<{
    id: number;
    name: string;
    values: {
      id: number;
      field_id: number;
      label: string;
      value: string;
    }[];
    created_by_user: null;
    created_at: string;
  }> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/list-filters/${id}`, payload, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete list filter
   */
  async deleteListFilter(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/list-filters/${id}`, { headers: API_VERSION })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  /**
   * Get labels
   */
  async getLabels(
    id: number,
    listData: ListDataParams
  ): Promise<ListDataResponse<ListLabel>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(`/lists/${id}/labels`, listData, {
          headers: API_VERSION
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data ?? [],
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create label
   */
  async createLabel(
    id: number,
    payload: { name: string }
  ): Promise<{
    id: number;
    name: string;
    order: number;
  }> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/lists/${id}/labels`, payload, { headers: API_VERSION })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete label
   */
  async deleteLabel(labelId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/list-labels/${labelId}`, {
          headers: API_VERSION
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  /**
   * Update list labels
   */
  async updateListLabels(
    listValueId: number,
    payload: { list_labels: { id: number; order: number }[] }
  ): Promise<ListLabel[]> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/list-values/${listValueId}/labels`, payload, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }
}

export { audiencesQueryKeys } from './query-keys';
export type {
  AudiencesListQueryParams,
  AudiencesListValuesParams
} from './query-keys';
export {
  useAudienceLabelsQuery,
  useAudienceListFiltersQuery,
  useAudienceListValuesQuery,
  useAudienceQuery,
  useAudiencesFieldGroupListTypesQuery,
  useAudiencesListQuery
} from './use-audiences-queries';
export {
  useAudienceCreateLabelMutation,
  useAudienceCreateMutation,
  useAudienceCreateOrUpdateListFilterMutation,
  useAudienceDeleteLabelMutation,
  useAudienceDeleteListFilterMutation,
  useAudienceDeleteListValuesMutation,
  useAudienceDeleteMutation,
  useAudienceRestoreMutation,
  useAudienceUpdateListLabelsMutation,
  useAudienceUpdateListValueMutation,
  useAudienceUpdateListValuesMutation,
  useAudienceUpdateMutation
} from './use-audiences-mutations';
